import { 
  Container, 
  Typography, 
  Box, 
  Grid,
  Paper,
  Divider,
  Card,
  CardContent,
  useTheme,
} from '@mui/material';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import BusinessIcon from '@mui/icons-material/Business';
import VerifiedIcon from '@mui/icons-material/Verified';
import SpeedIcon from '@mui/icons-material/Speed';

const FeaturesPage = () => {
  const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const listItems = [
    "1 Billion+ Profiles and Resumes Analyzed - Unlock access to global talent in seconds",
    "AI-Powered Resume Analysis - Instantly screen candidates for quality and relevance",
    "Smart Candidate Matching - Aligns candidate fit with job requirements using real-world hiring data",
    "Intelligent Learning System - Learns and adapts to your hiring patterns for ongoing improvement",
    "Time-Saving Automation - Reduce time-to-hire and free up your internal team",
    "Bias-Free Recruiting - Ensure fair, equitable shortlists with impartial AI",
    "Comprehensive Resume Scoring - Know who to prioritize instantly",
    "Jobs Posted on up to 75+ Job Boards - Giving you access to both active and passive candidates",
  ];

  const benefitItems = [
    "A large footprint in 20+ cities",
    "50,000 candidates placed",
    "7,000 clients served",
    "40+ years finding the best talent"
  ];

  const forbesRankings = [
    "America's Best Professional Recruiting Firms",
    "Top 200 Executive Search Firms",
    "Top 250 Contract Staffing Firms"
  ];

  // Helper function to extract numeric values from benefit items
  const extractNumericValue = (benefit: string) => {
    // Match numbers with optional comma and plus sign
    const match = benefit.match(/(\d{1,3}(?:,\d{3})*\+?)/);
    return match ? match[0] : "";
  };

  // Helper function to get the text after the numeric value
  const getTextAfterNumber = (benefit: string) => {
    // Replace the numeric part (including + if present) and any leading spaces
    return benefit.replace(/\d{1,3}(?:,\d{3})*\+?\s*/, '');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.light}15, ${theme.palette.primary.main}10)`,
          border: `1px solid ${theme.palette.primary.light}30`
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography 
            variant="h4" 
            component="div"
            sx={{ 
              color: 'primary.main',
              fontWeight: 600,
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              letterSpacing: 1.5,
              mb: 2,
              textTransform: 'uppercase'
            }}
          >
            RECRUITMENT INTELLIGENCE® Features
          </Typography>
          {/* <Divider sx={{ width: '80px', mx: 'auto', mb: 3, borderWidth: 2, borderColor: 'primary.main' }} /> */}
        </Box>

        <Typography variant="h5" component="h1" gutterBottom sx={{ 
          fontWeight: 600, 
          textAlign: 'center',
          fontSize: { xs: '1.5rem', md: '1.75rem' }
        }}>
          Why RECRUITMENT INTELLIGENCE®?
        </Typography>
        
        <Typography variant="body1" paragraph sx={{ 
          textAlign: 'center', 
          maxWidth: '900px', 
          mx: 'auto',
          fontSize: '1.05rem',
          lineHeight: 1.6
        }}>
          RECRUITMENT INTELLIGENCE® (RiC) is the only AI hiring solution developed and backed by <strong>American Recruiting & Consulting Group (ARC)</strong>, a Forbes-ranked, award-winning professional recruitment firm with over 40+ years of proven success.
        </Typography>
        
        <Typography variant="body1" paragraph sx={{ 
          textAlign: 'center', 
          maxWidth: '900px', 
          mx: 'auto',
          fontSize: '1.05rem',
          lineHeight: 1.6
        }}>
          Built by real recruiters, not just developers and algorithms, RiC combines advanced artificial intelligence with deep industry expertise to deliver a faster, smarter, and more accurate hiring experience.
        </Typography>
      </Paper>

      {/* AI Powered Section */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <EmojiObjectsIcon sx={{ color: 'primary.main', mr: 1, fontSize: 30 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            AI Powered, Human-Perfected
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Typography variant="body1" gutterBottom sx={{ mb: 3, fontSize: '1.05rem' }}>
          RiC offers a comprehensive suite of AI-powered tools designed to revolutionize your hiring process:
        </Typography>

        <Grid container spacing={3}>
          {listItems.map((text, index) => {
            const parts = text.split(' - ');
            
            return (
              <Grid item xs={12} md={6} key={index}>
                <Card elevation={1} sx={{ 
                  height: '100%', 
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex' }}>
                      <Box sx={{ mr: 1.5, mt: 0.5 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {parts[0]}
                        </Typography>
                        {parts.length > 1 && (
                          <Typography variant="body2" color="text.secondary">
                            {parts[1]}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Backed by the Best Section */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <BusinessIcon sx={{ color: 'primary.main', mr: 1, fontSize: 30 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Backed by the Best in the Business
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Typography variant="body1" sx={{ mb: 3, fontSize: '1.05rem' }}>
          Recruitment Intelligence® was created by the professionals at <strong>American Recruiting & Consulting Group</strong>, a firm consistently ranked by Forbes as one of:
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {forbesRankings.map((ranking, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 3, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  borderTop: `4px solid ${theme.palette.primary.main}`,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)'
                  }
                }}
              >
                <VerifiedIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {ranking}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Other Benefits Section */}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SpeedIcon sx={{ color: 'primary.main', mr: 1, fontSize: 30 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Other Benefits
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {benefitItems.map((benefit, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box 
                sx={{ 
                  p: 2, 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: `${theme.palette.primary.main}08`
                  }
                }}
              >
                <Typography variant="h3" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
                  {extractNumericValue(benefit)}
                  {benefit.includes('years') }
                </Typography>
                <Typography variant="body2">
                  {getTextAfterNumber(benefit)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box 
          sx={{ 
            p: 3, 
            mt: 2, 
            textAlign: 'center',
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            borderRadius: 2
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Smarter Hiring Starts Here
          </Typography>
          <Typography variant="body1">
            Experience the power of AI, refined by decades of human recruiting expertise.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default FeaturesPage;
